/**
 * MetricCard — a uniform KPI tile (label · big value · optional sub-line).
 *
 * Presentation-only and theme-token based (works in light + dark). Use for
 * dashboard KPIs / performance stats so every metric tile shares one look.
 */
import React from 'react';
import { CH_TYPE } from '../../styles/appUi';
import { Card, Box, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export type MetricTone = 'default' | 'bull' | 'bear' | 'warn' | 'muted';

interface Props {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: MetricTone;
  /** Optional leading icon shown next to the label. */
  icon?: React.ReactNode;
  /** Optional help text shown as an (i) tooltip next to the label. */
  help?: string;
  /** Compact padding for dense rows of tiles. */
  dense?: boolean;
  align?: 'left' | 'center';
}

const toneColor: Record<MetricTone, string> = {
  default: 'text.primary',
  bull: 'success.main',
  bear: 'error.main',
  warn: 'warning.main',
  muted: 'text.secondary',
};

const MetricCard: React.FC<Props> = ({ label, value, sub, tone = 'default', icon, help, dense, align = 'left' }) => (
  <Card sx={{ p: dense ? 1.25 : 1.75, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
      {icon}
      <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' }}>
        {label}
      </Typography>
      {help && (
        <Tooltip title={help} arrow>
          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      )}
    </Box>
    <Typography sx={{ fontSize: dense ? '1.15rem' : '1.4rem', fontWeight: 800, lineHeight: 1.1, color: toneColor[tone], textAlign: align }}>
      {value}
    </Typography>
    {sub != null && (
      <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', textAlign: align }}>
        {sub}
      </Typography>
    )}
  </Card>
);

export default MetricCard;
